import { useState, useEffect } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import {
  ClipboardCheck, Check, FileText, Shield, Monitor, GraduationCap, Users,
} from "lucide-react";
import api from "../../lib/api";

const categoryIcons = {
  documents: FileText,
  compliance: Shield,
  it_setup: Monitor,
  training: GraduationCap,
  general: Users,
};

const categoryColors = {
  documents: "bg-blue-50 text-blue-700 border-blue-200",
  compliance: "bg-red-50 text-red-700 border-red-200",
  it_setup: "bg-violet-50 text-violet-700 border-violet-200",
  training: "bg-amber-50 text-amber-700 border-amber-200",
  general: "bg-green-50 text-green-700 border-green-200",
};

const MyOnboarding = () => {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOnboarding();
  }, []);

  const fetchOnboarding = async () => {
    try {
      const res = await api.get("/self-service/onboarding");
      setProcesses(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load onboarding");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (processes.length === 0) {
    return (
      <div className="space-y-6" data-testid="my-onboarding-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">My Onboarding</h1>
          <p className="text-slate-500">Track your onboarding progress</p>
        </div>
        <div className="text-center py-16 text-slate-500">
          <ClipboardCheck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p>No onboarding process assigned to you yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-onboarding-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-heading">My Onboarding</h1>
        <p className="text-slate-500">Track your onboarding progress</p>
      </div>

      {processes.map(proc => {
        const items = proc.items || [];
        const completedCount = items.filter(i => i.completed).length;
        const progress = items.length ? Math.round((completedCount / items.length) * 100) : 0;

        return (
          <Card key={proc.id} className="card-soft" data-testid={`onboarding-process-${proc.id}`}>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-800">Onboarding Checklist</h3>
                    <Badge variant="outline" className={
                      proc.status === "completed" ? "bg-green-50 text-green-700 border-green-200" :
                      "bg-amber-50 text-amber-700 border-amber-200"
                    }>{proc.status === "completed" ? "Completed" : "In Progress"}</Badge>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">Started {new Date(proc.started_at).toLocaleDateString()}</p>
                </div>
                <p className="text-sm font-medium text-slate-600">{completedCount}/{items.length}</p>
              </div>

              <Progress value={progress} className="h-2.5" />

              <div className="space-y-2">
                {items.map((item, idx) => {
                  const CatIcon = categoryIcons[item.category] || ClipboardCheck;
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                        item.completed ? "bg-green-50/50 border-green-200" : "bg-white border-slate-200"
                      }`}
                      data-testid={`onboarding-item-${idx}`}
                    >
                      <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        item.completed ? "bg-green-500 border-green-500 text-white" : "border-slate-300"
                      }`}>
                        {item.completed && <Check className="h-3.5 w-3.5" />}
                      </div>
                      <CatIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${item.completed ? "text-green-700 line-through" : "text-slate-800"}`}>
                          {item.name}
                        </p>
                        {item.description && <p className="text-xs text-slate-400">{item.description}</p>}
                      </div>
                      <Badge variant="outline" className={`text-[10px] ${categoryColors[item.category] || categoryColors.general}`}>
                        {item.category}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default MyOnboarding;
